import { createSelector } from 'reselect';
import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import {
  IOrganisationUser,
  IOrganisationUserDAO,
  IOrgUserCertificate,
  IOrgUserCertificateReport,
  IOrgUserVariableOverride,
  IOrgUserVariableDAO,
} from 'types/domain/user';
import { IEntityReducer } from 'core/reducers/entities';
import { getEntities } from '../utils/getEntities';
import { parseCertificateDAO } from './certificate';
import { IKeyArray } from 'types/IKeyArray';
import { reduceArrayById } from 'utils/reduceArrayById';
import { maxBy } from 'lodash';
import { convertDate } from 'utils/convertDate';
import { ISearchReducer } from 'core/slices/search';
import { SearchType } from 'types/domain/search';
import { sortByNameModified } from './latestUsersReport';
import { ReportType } from 'core/slices/report';
import { parseVariableDAO } from './variable';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => (id ? id : state.state[EntityTypes.ORG_USER]);
const search = (state: RootState) => state.core.search;
const report = (state: RootState) => state.core.report.reports[ReportType.MANAGE_USERS];

export const selectUser = createSelector([entity, id], parseUserDAO);
export const selectUserReport = createSelector([entity, id], parseUserReport);

export const selectUsers = createSelector(
  [entity, search, report],
  (entity: IEntityReducer, search: ISearchReducer, report) => {
    return Object.values(entity[EntityTypes.ORG_USER])
      .filter((g: IOrganisationUser) =>
        search.searchType === SearchType.ORG_USER && search.term
          ? (g.firstName && g.firstName.toLowerCase().includes(search.term.toLocaleLowerCase())) ||
            (g.lastName && g.lastName.toLowerCase().includes(search.term.toLocaleLowerCase())) ||
            (g.email && g.email.toLowerCase().includes(search.term.toLocaleLowerCase()))
          : true,
      )
      .sort((a, b) => sortByNameModified(a, b, search, report))
      .map((user: IOrganisationUser) => parseUserDAO(entity, user.id));
  },
);

export const selectUserSignIn = createSelector([entity], (entity: IEntityReducer) => {
  return Object.values(entity[EntityTypes.ORG_USER])
    .filter(g => g.status > -1)
    .filter(g => g.signin != null)
    .filter(g => g.signin.lastLoginSignin.latitude != 0)

    .map((user: IOrganisationUserDAO) => parseUserDAO(entity, user.id));
});

export function parseUserDAO(entity: IEntityReducer, id: number) {
  const user: IOrganisationUser = entity[EntityTypes.ORG_USER][id];

  if (!user) return null;

  return {
    ...user,
    name: user.firstName ? `${user.firstName} ${user.lastName}` : user.email,
    roleConfig: user.roleConfigId ? entity[EntityTypes.ROLE_CONFIG][user.roleConfigId] : '',
    groupUsers: getEntities(entity, EntityTypes.GROUP_USER, 'organisationUserId', id),
    signin: user.signin,
  } as IOrganisationUserDAO;
}

export function parseUserReport(entity: IEntityReducer, id: number) {
  const user: IOrganisationUserDAO = parseUserDAO(entity, id);

  if (!user) return null;
  // const accessPermissions = getEntities(entity, EntityTypes.COURSE_PROGRESS, 'organisationUserId', user.id).map(ap =>
  //   parseAccessPermission(entity, ap.id),
  // );
  const organisationUserVariables: IKeyArray<IOrgUserVariableDAO> = reduceArrayById(
    getEntities(entity, EntityTypes.ORG_USER_VARIABLE, 'organisationUserId', user.id).map(orv => {
      return { ...orv, variable: parseVariableDAO(entity, orv.variableId) };
    }),
    'variableId',
  );
  const variableOverrides = getEntities(entity, EntityTypes.ORG_USER_VARIABLE_OVER, 'organisationUserId', user.id);
  const variableOverridesByVId: IKeyArray<IOrgUserVariableOverride> = reduceArrayById(variableOverrides, 'variableId');
  const certificates: IOrgUserCertificate[] = getEntities(
    entity,
    EntityTypes.ORG_USER_CERTIFICATE,
    'organisationUserId',
    user.id,
  );

  const certificateReports: IKeyArray<IOrgUserCertificateReport> = certificates.reduce(
    (prev, cert: IOrgUserCertificate) => {
      const certificate = parseCertificateDAO(entity, cert.certificateId);

      if (!certificate) return prev;

      // const variableGoals = certificate.variableGoals.map((variable: ICourseVariableDAO) => {
      //   return {
      //     variable,
      //     orgUserVariable: organisationUserVariables[variable.variableId],
      //     orgUserVariableOverride: variableOverridesByVId[variable.variableId],
      //     complete: isVariableComplete(
      //       variable,
      //       organisationUserVariables[variable.variableId],
      //       variableOverridesByVId[variable.variableId],
      //     ),
      //   } as IOrgUserCourseVariable;
      // });

      return {
        ...prev,
        [cert.certificateId]: {
          ...cert,
          certificate,
          // progress: variableGoals.reduce((prev, next) => (next.complete ? prev + 1 : prev), 0) / variableGoals.length,
          // variableGoals,
          organisationUserVariables,
        } as IOrgUserCertificateReport,
      };
    },
    {},
  );

  return {
    ...user,
    certificateReports,
    certificates,
    organisationUserVariables,
    variableOverrides,
    // accessPermissions,
    // lastAccessPermission: maxBy(accessPermissions, accessPerm => convertDate(accessPerm.modified?.date)),
    lastVariable: maxBy(Object.values(organisationUserVariables), variable => convertDate(variable.modified?.date)),
    lastCertificate: maxBy(Object.values(certificateReports), certificate => convertDate(certificate.modified?.date)),
  };
}
